import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { match } from 'ts-pattern';
import ActionButton from '@/components/Button/ActionButton';
import { Card } from '@/components/Card/card';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import DownloadTemplateCard from '@/features/import-csv/-components/-upload-file/DownloadTemplateCard';
import type { ExportApiUrl } from '@/features/import-csv/types';
import { ImportStepperHeader, isImportConflictError, UploadFileCard, type UploadedCsvFile, useEnqueueImportJob } from '@/features/people-csv-import';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import type { ImportType } from '@/lib/api/orpc/contracts/people-csv-async-import';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CsvImportUploadSearchSchema } from './-zod-schema';
const CsvImportUpload = () => {
  const {
    type
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    toast
  } = useToast();
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });
  const {
    t: tWorkerType
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.importCsv.workerType'
  });
  const [uploadedFile, setUploadedFile] = useState<UploadedCsvFile | undefined>(undefined);
  const {
    mutate: enqueue,
    isPending
  } = useEnqueueImportJob(type);
  const exportApiUrl = match<ImportType, ExportApiUrl>(type).with('EMPLOYEE', () => '/v2/people-csv/employee').with('CONTRACTOR', () => '/v2/people-csv/contractor').with('BOARD_MEMBER', () => '/v2/people-csv/board-member').with('COLLABORATOR', () => '/v2/people-csv/collaborator').exhaustive();
  const exportFilename = match<ImportType, string>(type).with('EMPLOYEE', () => tWorkerType('filename.employee')).with('CONTRACTOR', () => tWorkerType('filename.contractor')).with('BOARD_MEMBER', () => tWorkerType('filename.boardMember')).with('COLLABORATOR', () => tWorkerType('filename.collaborator')).exhaustive();
  const templateTitle = match<ImportType, string>(type).with('EMPLOYEE', () => tWorkerType('prepareCsvFile.templateTitle.employee')).with('CONTRACTOR', () => tWorkerType('prepareCsvFile.templateTitle.contractor')).with('BOARD_MEMBER', () => tWorkerType('prepareCsvFile.templateTitle.boardMember')).with('COLLABORATOR', () => tWorkerType('prepareCsvFile.templateTitle.collaborator')).exhaustive();
  const updateExisting = match<ImportType, string>(type).with('EMPLOYEE', () => tWorkerType('prepareCsvFile.updateExisting.employee')).with('CONTRACTOR', () => tWorkerType('prepareCsvFile.updateExisting.contractor')).with('BOARD_MEMBER', () => tWorkerType('prepareCsvFile.updateExisting.boardMember')).with('COLLABORATOR', () => tWorkerType('prepareCsvFile.updateExisting.collaborator')).exhaustive();
  const handleUpload = () => {
    if (!uploadedFile) {
      return;
    }
    enqueue({
      uploadedFileKey: uploadedFile.uploadedFileKey,
      originalFileName: uploadedFile.originalFileName,
      encoding: 'UTF-8'
    }, {
      onSuccess: ({
        importId
      }) => {
        navigate({
          to: '/person/csv-import/$importId',
          params: {
            importId
          }
        });
      },
      onError: error => {
        toast({
          description: isImportConflictError(error) ? t('errors.conflict') : t('errors.enqueueFailed'),
          variant: 'destructive'
        });
      }
    });
  };
  return <VStack className="w-full items-start gap-6 p-10">
      <HStack className="items-center gap-1">
        <Link to="/person/csv-import" search={{
        type
      }}>
          <Text variant="labelReg" className="text-neutral-muted-on-fill">
            {t('breadcrumb')}
          </Text>
        </Link>
      </HStack>

      <ImportStepperHeader currentStep={1} />

      <DownloadTemplateCard exportApiUrl={exportApiUrl} exportFilename={exportFilename} templateMessage={{
      templateTitle,
      updateExisting
    }} />
      <UploadFileCard onUploaded={setUploadedFile} />

      <Spacer size={8} />

      <Card className="w-full">
        <HStack className="w-full items-center justify-between px-6 py-4">
          <Link to="/person/csv-import" search={{
          type
        }}>
            <ActionButton variant="ghost">{tc('action.cancel')}</ActionButton>
          </Link>
          <ActionButton variant="default" icon="ArrowRightIcon" iconPosition="right" isLoading={isPending} disabled={!uploadedFile} onClick={handleUpload}>
            {tc('action.next')}
          </ActionButton>
        </HStack>
      </Card>
    </VStack>;
};
export const Route = createFileRoute('/_staff/person/csv-import/upload')({
  validateSearch: zodValidator(CsvImportUploadSearchSchema),
  component: CsvImportUpload
});